import { useMemo } from 'react'
import { Combobox } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'
import { useFacilityLocations } from '@/hooks/useFacility'
import { floorOptions } from '@/lib/facilityLocation'

const NONE = '__none__'

/**
 * Pick the floor a room is on. The room's place: the facility and building
 * come with the floor, so nothing about where it is is typed in free text
 * (NYP feedback, 2026-10-29). The same field Building set-up maps by drag, so
 * either one moves it.
 */
export function FloorPicker({
  value,
  onChange,
}: {
  value: string | null
  onChange: (floorId: string | null) => void
}) {
  const locations = useFacilityLocations()
  const floors = useMemo(() => (locations.data ? floorOptions(locations.data) : []), [locations.data])
  const options = useMemo(() => [{ value: NONE, label: 'Not on a floor yet' }, ...floors], [floors])

  // Nothing to pick from yet. An editable field here would show a room's
  // current floor as blank, and leaving it would clear that floor on save, so
  // it stays read-only until the floors are in.
  if (!locations.data) {
    return (
      <div className='space-y-1'>
        <Input disabled value='' placeholder={locations.isError ? 'Floors could not be loaded' : 'Loading floors…'} />
        {locations.isError && <p className='text-xs text-destructive'>Floors could not be loaded.</p>}
      </div>
    )
  }

  return (
    <div className='space-y-1'>
      <Combobox
        value={value ?? NONE}
        options={options}
        placeholder='Type to search floors'
        onChange={(v) => onChange(v && v !== NONE ? v : null)}
      />
      {floors.length === 0 && (
        <p className='text-xs text-amber-700'>No floors yet. Add buildings and floors in Building set-up.</p>
      )}
    </div>
  )
}
